import {
  signUp,
  confirmSignUp,
  signIn,
  signOut,
  resetPassword,
  confirmResetPassword,
  fetchAuthSession,
  fetchUserAttributes,
  resendSignUpCode,
  deleteUser,
  signInWithRedirect,
} from 'aws-amplify/auth';
import { IUser, User, userT } from '../../../types/auth';
import { AuthTokens, getCurrentUser, updatePassword } from '@aws-amplify/auth';
import { AUTH_CONSTANTS } from '../../../config/constants';
import { logger } from '../../../utils/logger';
import { tokenService } from './token.service';

// Session storage keys for OAuth signup flow
const OAUTH_SIGNUP_DATA_KEY = 'oauth_signup_data';

export interface OAuthSignupData {
  companyName: string;
  firstName: string;
  planType: string;
  planId: string;
  isTrialEnabled: boolean;
}

// Helper to store signup data before OAuth redirect
export const storeOAuthSignupData = (data: OAuthSignupData): void => {
  sessionStorage.setItem(OAUTH_SIGNUP_DATA_KEY, JSON.stringify(data));
};

// Helper to retrieve and clear signup data after OAuth callback
export const getOAuthSignupData = (): OAuthSignupData | null => {
  const data = sessionStorage.getItem(OAUTH_SIGNUP_DATA_KEY);
  return data ? JSON.parse(data) : null;
};

export const clearOAuthSignupData = (): void => {
  sessionStorage.removeItem(OAUTH_SIGNUP_DATA_KEY);
};

export class CognitoService {
  constructor() {}

  static async signIn(email: string, password: string) {
    try {
      // Clear existing token
      tokenService.clearToken();

      // Ensure we're signed out before signing in
      const currentUser = await getCurrentUser().catch(() => null);
      if (currentUser) {
        await signOut({ global: true });
      }

      // Attempt to sign in
      const { isSignedIn, nextStep } = await signIn({ username: email, password });

      if (!isSignedIn) {
        return { isSignedIn, nextStep };
      }

      // Fetch user attributes
      const attributes = await fetchUserAttributes();

      // Construct user object
      const user: User = {
        id: attributes.sub,
        email: attributes.email || email,
        userName: attributes.name || email,
      };

      // Determine user role (including DEALER_ADMIN)
      const customRole = attributes['custom:role'];
      let role: string;
      switch (customRole) {
        case 'admin':
          role = AUTH_CONSTANTS.ROLES.SUPER_ADMIN;
          break;
        case 'dealer_admin':
          role = AUTH_CONSTANTS.ROLES.DEALER_ADMIN;
          break;
        case 'client_admin':
          role = AUTH_CONSTANTS.ROLES.ADMIN;
          break;
        default:
          role = AUTH_CONSTANTS.ROLES.USER;
      }

      return {
        user,
        role,
        token: '',
        clientId: attributes['custom:clientId'],
        promoCode: attributes['custom:promoCode'],
      };
    } catch (error) {
      logger.error('Auth error:', error);
      throw error;
    }
  }

  static async signUp(user: userT): Promise<any> {
    try {
      const userAttributes: Record<string, string> = {
        'custom:clientId': user.orgId,
        'custom:role': user.role,
      };

      // Add promoCode for dealer_admin users
      if (user.promoCode) {
        userAttributes['custom:promoCode'] = user.promoCode;
      }

      const result = await signUp({
        username: user.email,
        password: user.password || import.meta.env.VITE_TEMP_PASSWORD,
        options: {
          userAttributes,
        },
      });
      return result;
    } catch (error) {
      throw error;
    }
  }

  static async confirmCode(username: string, confirmationCode: string): Promise<any> {
    const result = await confirmSignUp({
      username,
      confirmationCode,
    });
    return result;
  }

  static async signOut() {
    try {
      try {
        const currentUser = await getCurrentUser();
        if (currentUser) {
          await signOut({ global: true });
          await tokenService.clearToken();
        }
      } catch (error) {
        logger.error('Sign out error:', error);
      }
      await signOut({ global: true });
    } catch (error) {
      logger.error('Sign out error:', error);
      throw error;
    }
  }

  static async getCurrentUser() {
    try {
      // First check if there's a valid session
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;

      if (!idToken) {
        // No valid session
        return null;
      }

      let attributes: Record<string, string | undefined> = {};

      // Check if this is an OAuth user by looking at the token payload
      const payload = idToken.payload;
      const isOAuthUser =
        payload['cognito:username']?.toString().startsWith('google_') ||
        payload['identities'] !== undefined;

      if (isOAuthUser) {
        // For OAuth users, use ID token payload directly (fetchUserAttributes will fail)
        logger.info('OAuth user detected, using ID token for getCurrentUser');
        console.log('[getCurrentUser] OAuth user ID token payload:', {
          sub: payload.sub,
          email: payload.email,
          name: payload.name,
          'cognito:username': payload['cognito:username'],
          'custom:clientId': payload['custom:clientId'],
          'custom:role': payload['custom:role'],
          'custom:promoCode': payload['custom:promoCode'],
          identities: payload['identities'],
        });
        attributes = {
          sub: payload.sub as string,
          email: payload.email as string,
          name: payload.name as string,
          'custom:clientId': payload['custom:clientId'] as string | undefined,
          'custom:role': payload['custom:role'] as string | undefined,
          'custom:promoCode': payload['custom:promoCode'] as string | undefined,
        };
      } else {
        // For native Cognito users, try fetchUserAttributes
        try {
          const fetchedAttrs = await fetchUserAttributes();
          attributes = fetchedAttrs as Record<string, string | undefined>;
        } catch (attrError) {
          // Fallback to ID token if fetchUserAttributes fails
          logger.info('fetchUserAttributes failed, using ID token fallback');
          attributes = {
            sub: payload.sub as string,
            email: payload.email as string,
            name: payload.name as string,
            'custom:clientId': payload['custom:clientId'] as string | undefined,
            'custom:role': payload['custom:role'] as string | undefined,
            'custom:promoCode': payload['custom:promoCode'] as string | undefined,
          };
        }
      }

      const user: User = {
        id: attributes.sub,
        email: attributes.email || '',
        userName: attributes.name || attributes.email || '',
      };

      // Determine user role (including DEALER_ADMIN)
      const customRole = attributes['custom:role'];
      let role: string;
      switch (customRole) {
        case 'admin':
          role = AUTH_CONSTANTS.ROLES.SUPER_ADMIN;
          break;
        case 'dealer_admin':
          role = AUTH_CONSTANTS.ROLES.DEALER_ADMIN;
          break;
        case 'client_admin':
          role = AUTH_CONSTANTS.ROLES.ADMIN;
          break;
        default:
          role = AUTH_CONSTANTS.ROLES.USER;
      }

      console.log('[getCurrentUser] Final result:', {
        email: user.email,
        customRole: attributes['custom:role'],
        resolvedRole: role,
        clientId: attributes['custom:clientId'],
        isOAuthUser,
      });

      return {
        user,
        role,
        clientId: attributes['custom:clientId'],
        promoCode: attributes['custom:promoCode'],
      };
    } catch {
      return null;
    }
  }

  static async forgetPassword(email: string): Promise<any> {
    try {
      const result = await resetPassword({
        username: email,
      });
      return result;
    } catch (error) {
      throw error;
    }
  }

  static async confirmNewPassword(user: IUser): Promise<any> {
    try {
      const result = await confirmResetPassword({
        username: user.email,
        confirmationCode: user.code || '',
        newPassword: user.newPassword || '',
      });
      return result;
    } catch (error) {
      throw error;
    }
  }

  static async logOut(): Promise<any> {
    localStorage.clear();
    return signOut({ global: true });
  }

  async getCurrentSession(): Promise<AuthTokens | undefined> {
    return (await fetchAuthSession({ forceRefresh: true })).tokens;
  }

  async getCurrentSessionDetails(): Promise<any> {
    const fetchAuthSessionDetails = await fetchAuthSession();
    return fetchAuthSessionDetails;
  }

  static async updatePassword(user: any): Promise<any> {
    try {
      const result = await updatePassword({
        oldPassword: user.password || '',
        newPassword: user.newPassword || '',
      });
      return result;
    } catch (error) {
      throw error;
    }
  }

  static async deleteUser(): Promise<void> {
    try {
      // Delete the user from Cognito
      await deleteUser();
      // Optionally, clear any local tokens or session data
      tokenService.clearToken();
    } catch (error) {
      logger.error('Error deleting user:', error);
      throw error;
    }
  }

  static async resendSignUpCode(email: string): Promise<any> {
    try {
      const results = await resendSignUpCode({
        username: email,
      });
      return results;
    } catch (error) {
      throw error;
    }
  }

  static async userSignTemp(email: string, password: string): Promise<any> {
    const result = await signIn({
      username: email,
      password: password,
    });
    return result;
  }

  /**
   * Initiate Google Sign-In redirect
   * This will redirect the user to Google's OAuth flow
   */
  static async signInWithGoogle(): Promise<void> {
    try {
      // Clear any existing tokens
      tokenService.clearToken();

      // Initiate the OAuth redirect
      await signInWithRedirect({ provider: 'Google' });
    } catch (error) {
      logger.error('Google sign-in error:', error);
      throw error;
    }
  }

  /**
   * Handle the OAuth callback after Google sign-in
   * This should be called from the callback page
   */
  static async handleOAuthCallback(): Promise<{
    user: User;
    role: string;
    clientId: string | undefined;
    isNewUser: boolean;
    promoCode: string | undefined;
  } | null> {
    try {
      // Wait a moment for Amplify to process the callback
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Check if user is signed in
      const currentUser = await getCurrentUser().catch(() => null);
      if (!currentUser) {
        logger.info('No user found after OAuth callback');
        return null;
      }

      logger.info('Current user from OAuth:', {
        username: currentUser.username,
        userId: currentUser.userId,
      });

      // For OAuth users, we need to get user info from the ID token
      // because fetchUserAttributes may not work due to scope restrictions
      let attributes: Record<string, string | undefined> = {};

      try {
        // First try fetchUserAttributes (works for native Cognito users)
        const fetchedAttrs = await fetchUserAttributes();
        attributes = fetchedAttrs as Record<string, string | undefined>;
        logger.info('Fetched user attributes successfully');
      } catch (attrError) {
        // If fetchUserAttributes fails (common for OAuth users),
        // get user info from the ID token instead
        logger.info('fetchUserAttributes failed, using ID token:', attrError);

        try {
          const session = await fetchAuthSession();
          const idToken = session.tokens?.idToken;

          if (idToken) {
            // Parse the ID token payload to get user info
            const payload = idToken.payload;
            attributes = {
              sub: payload.sub as string,
              email: (payload.email as string) || '',
              name: (payload.name as string) || '',
              given_name: (payload.given_name as string) || '',
              family_name: (payload.family_name as string) || '',
              'custom:clientId': payload['custom:clientId'] as string | undefined,
              'custom:role': payload['custom:role'] as string | undefined,
              'custom:promoCode': payload['custom:promoCode'] as string | undefined,
            };
            logger.info('Got user info from ID token');
          }
        } catch (sessionError) {
          logger.error('Failed to get auth session:', sessionError);
          throw new Error('Unable to retrieve user information');
        }
      }

      const user: User = {
        id: attributes.sub || currentUser.userId,
        email: attributes.email || '',
        userName: attributes.name || attributes.email || '',
        firstName: attributes.given_name || attributes.name?.split(' ')[0] || '',
      };

      // Check if user has custom attributes (existing user vs new OAuth user)
      const clientId = attributes['custom:clientId'];
      const customRole = attributes['custom:role'];
      const promoCode = attributes['custom:promoCode'];

      // Determine role (including DEALER_ADMIN)
      let role: string;
      if (customRole) {
        switch (customRole) {
          case 'admin':
            role = AUTH_CONSTANTS.ROLES.SUPER_ADMIN;
            break;
          case 'dealer_admin':
            role = AUTH_CONSTANTS.ROLES.DEALER_ADMIN;
            break;
          case 'client_admin':
            role = AUTH_CONSTANTS.ROLES.ADMIN;
            break;
          default:
            role = AUTH_CONSTANTS.ROLES.USER;
        }
      } else {
        role = AUTH_CONSTANTS.ROLES.ADMIN; // Default to admin for new OAuth users
      }

      // isNewUser is true if the user doesn't have a clientId set
      const isNewUser = !clientId;

      logger.info('OAuth callback processed', {
        email: user.email,
        hasClientId: !!clientId,
        isNewUser,
      });

      return { user, role, clientId, isNewUser, promoCode };
    } catch (error) {
      logger.error('OAuth callback error:', error);
      throw error;
    }
  }

  /**
   * Check if the current user is an OAuth (federated) user
   */
  static async isOAuthUser(): Promise<boolean> {
    try {
      const currentUser = await getCurrentUser();
      // OAuth users typically have usernames that start with the provider name
      // e.g., "Google_123456789" or their username matches their sub
      const username = currentUser.username;
      return (
        username.startsWith('Google_') ||
        username.startsWith('google_') ||
        username.startsWith('Facebook_') ||
        username.startsWith('SignInWithApple_')
      );
    } catch {
      return false;
    }
  }
}
// resendUserSignUpCode
// cofirm signup
